import React, { memo, useEffect, useMemo, useState } from 'react';

import { Box } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import { usePageContentContext } from 'components/Layout';
import { SideNavMenuItemProps } from 'components/Navigation';
import { ActiveResourceProvider } from 'containers/ActiveCloudResource/hooks/context/ActiveResourceProvider';
import {
  selectActiveCloudAccount,
  selectActiveEnvironment,
} from 'containers/App/selectors';
import { InventorySidebar } from 'containers/Inventory/CloudInventory/Components/InventorySideNav/InventorySidebar';
import { useInventory } from 'containers/Inventory/CloudInventory/hooks/useInventory';
import { actions as sharedStateActions } from 'containers/SharedState/slice';
import { useInjector } from 'utils/inject';

import {
  InventoryContextProvider,
  InventoryContextProps,
} from './hooks/context';
import { inventorySaga } from './saga';
import { actions, reducer, sliceKey } from './slice';

interface Props {
  useInventoryV3?: boolean;
}

export const CloudInventory = memo(({ useInventoryV3 = false }: Props) => {
  useInjector(sliceKey, reducer, inventorySaga);
  const { accountId } = useSelector(selectActiveCloudAccount);
  const { environmentId } = useSelector(selectActiveEnvironment);
  const { contentHeight } = usePageContentContext();
  const dispatch = useDispatch();
  const { handleCategoryChange } = useInventory();

  const [showInventorySummary, setShowInventorySummary] = useState(false);
  const [
    showCategoryInventorySummary,
    setShowCategoryInventorySummary,
  ] = useState(false);
  const [isOpenAddContext, setIsOpenAddContext] = useState(false);
  // Removed isOpenAddWhitelist state: no consumer read it (modals use local disclosures).
  const [sortBy, setSortBy] = useState<string>();
  const [currentInstance, setCurrentInstance] = useState<Record<string, any>>(
    {},
  );
  const [selectedRegion, setSelectedRegion] = useState<Record<string, any>>({
    label: 'Region',
    value: undefined,
  });
  const [selectedVpc, setSelectedVpc] = useState<Record<string, any>>({
    label: 'VPC',
    value: undefined,
  });
  const [sideNavItemsMapping, setSideNavItemsMapping] = useState<
    Record<string, SideNavMenuItemProps>
  >({});

  const [menuItems, updateMenuItems] = useState<SideNavMenuItemProps[]>([]);

  useEffect(() => {
    dispatch(sharedStateActions.getRegions({ q: { useInventoryV3 } } as any));
  }, [dispatch, useInventoryV3]);

  useEffect(() => {
    dispatch(actions.resetCurrentCategory());
    dispatch(actions.resetNativeResource());
    if (!!accountId || !!environmentId) {
      dispatch(
        actions.getResourceCategories({
          q: { accountId, environmentId, useInventoryV3 },
        } as any),
      );
      dispatch(actions.resetSearchItems());
    }
  }, [dispatch, accountId, environmentId, useInventoryV3]);

  const contextValue: InventoryContextProps = useMemo(
    () => ({
      useInventoryV3,
      currentInstance,
      setCurrentInstance,
      isOpenAddContext,
      setIsOpenAddContext,
      showInventorySummary,
      setShowInventorySummary,
      showCategoryInventorySummary,
      setShowCategoryInventorySummary,
      sortBy,
      setSortBy,
      sideNavItemsMapping,
      setSideNavItemsMapping,
      handleCategoryChange,
      selectedRegion,
      selectedVpc,
      setSelectedVpc,
      setSelectedRegion,
      updateMenuItems,
      menuItems,
    }),
    [
      useInventoryV3,
      currentInstance,
      isOpenAddContext,
      showInventorySummary,
      showCategoryInventorySummary,
      sortBy,
      sideNavItemsMapping,
      handleCategoryChange,
      selectedRegion,
      selectedVpc,
      updateMenuItems,
      menuItems,
    ],
  );

  return (
    <ActiveResourceProvider>
      <InventoryContextProvider value={contextValue}>
        <Box h={contentHeight - 8 + 'px'}>
          <InventorySidebar />
        </Box>
      </InventoryContextProvider>
    </ActiveResourceProvider>
  );
});
